import arv1440 from '../../assets/landing/gallery-arv-1440.webp'
import arv1440x2 from '../../assets/landing/gallery-arv-1440@2x.webp'
import arv360 from '../../assets/landing/gallery-arv-360.webp'
import arv360x2 from '../../assets/landing/gallery-arv-360@2x.webp'
import flip1440 from '../../assets/landing/gallery-flip-1440.webp'
import flip1440x2 from '../../assets/landing/gallery-flip-1440@2x.webp'
import flip360 from '../../assets/landing/gallery-flip-360.webp'
import flip360x2 from '../../assets/landing/gallery-flip-360@2x.webp'
import pipeline1440 from '../../assets/landing/gallery-pipeline-1440.webp'
import pipeline1440x2 from '../../assets/landing/gallery-pipeline-1440@2x.webp'
import pipeline360 from '../../assets/landing/gallery-pipeline-360.webp'
import pipeline360x2 from '../../assets/landing/gallery-pipeline-360@2x.webp'
import { Arrow } from './icons'

// The How-It-Works pages do not exist yet (spec K9). The links keep their drawn place but stay
// invisible and unfocusable until those routes ship; flip this and give each link a `to`.
const HOW_IT_WORKS_LIVE = false

const B = ({ children }) => <strong className="font-bold">{children}</strong>

// Figma 29.09 `why-choose_2` / `why-choose`, spec §2.6. Each PNG is the drawn crop, which includes
// the mini-cards floating outside the dark card: `cardTop` is how far the dark card sits below the
// crop's top at 1440, `textGap` the drawn step from the 360 crop's bottom to the title's line box.
const BLOCKS = [
  {
    title: 'ARV Analysis',
    tagline: 'Find the Strongest Comps',
    body: (
      <>
        Arvance ranks nearby properties by <B>similarity, distance, and recency</B>. Review the
        adjustments and choose which comps shape your ARV estimate.
      </>
    ),
    badges: ['Available nationwide'],
    link: 'How Arvance ranks comps',
    img: { d: arv1440, d2: arv1440x2, m: arv360, m2: arv360x2, mh: 251 },
    cardTop: 50,
    textGap: 'mt-[10px]',
  },
  {
    title: 'Flip Analysis',
    tagline: 'Forecast Profit Before You Commit',
    // D10: desktop bolds "calculate your" as well, mobile only the MAO phrase.
    body: (
      <>
        Compare financing, rehab, and exit scenarios,{' '}
        <span className="canvas:font-bold">calculate your</span> <B>maximum allowable offer (MAO)</B>,
        and see projected profit—without rebuilding your spreadsheet.
      </>
    ),
    badges: ['Editable assumptions', 'Custom templates'],
    link: 'How Deal Score works',
    img: { d: flip1440, d2: flip1440x2, m: flip360, m2: flip360x2, mh: 244 },
    cardTop: 20,
    textGap: 'mt-[7px]',
    imageRight: true,
  },
  {
    title: 'Deal Pipeline & Dashboard',
    tagline: 'Keep Every Deal Moving',
    body: 'Track stages, strategies, notes, and next steps from evaluation through disposition.',
    badges: ['Property stages', 'Custom tags'],
    link: null,
    img: { d: pipeline1440, d2: pipeline1440x2, m: pipeline360, m2: pipeline360x2, mh: 255 },
    cardTop: 54,
    textGap: 'mt-[9px]',
  },
]

const GalleryImage = ({ title, img, cardTop }) => (
  // Desktop: the box is the 612 x 405 dark card; the crop overhangs it by 20 on the sides.
  <div className="relative -mx-5 canvas:mx-0 canvas:w-[612px] canvas:h-[405px] canvas:shrink-0">
    <picture>
      <source media="(min-width: 640px)" srcSet={`${img.d} 652w, ${img.d2} 1304w`} sizes="(min-width: 1232px) 652px, 640px" />
      <img
        src={img.m}
        srcSet={`${img.m} 360w, ${img.m2} 720w`}
        sizes="(min-width: 640px) 640px, 100vw"
        alt={`${title} screen`}
        width="360"
        height={img.mh}
        loading="lazy"
        className="block w-full h-auto max-w-none canvas:absolute canvas:left-[-20px] canvas:top-[var(--card-top)] canvas:w-[652px]"
        style={{ '--card-top': `-${cardTop}px` }}
      />
    </picture>
  </div>
)

const GalleryText = ({ title, tagline, body, badges, link, textGap }) => (
  <div className={`${textGap} canvas:mt-0 canvas:w-[444px]`}>
    <h3 className="text-[24px] leading-[30px] canvas:text-[28px] canvas:leading-9 font-extrabold text-brand-dark">{title}</h3>
    <p className="text-[16px] leading-6 canvas:text-[18px] canvas:leading-7 font-semibold italic text-brand-dark">{tagline}</p>
    {/* D8: the desktop Pipeline copy is drawn 400, the others 500; all use 500 here. */}
    <p className="mt-3 canvas:mt-4 text-[16px] leading-6 canvas:text-[18px] canvas:leading-7 font-medium text-brand-dark">{body}</p>
    <ul className="mt-3 canvas:mt-4 flex flex-col canvas:flex-row items-start gap-3">
      {badges.map((badge) => (
        <li
          key={badge}
          className="inline-flex items-center h-7 canvas:h-8 px-2 rounded-[3.75px] canvas:rounded-[4px] bg-[#E9F0E5] border-[0.5px] border-brand-fieldBdr text-[16px] canvas:text-[18px] leading-6 font-semibold text-brand-dark whitespace-nowrap"
        >
          {badge}
        </li>
      ))}
    </ul>
    {link && (
      <span
        aria-hidden={!HOW_IT_WORKS_LIVE}
        className={`mt-[14px] canvas:mt-4 inline-flex items-center gap-3 h-7 canvas:h-8 px-2 text-[16px] canvas:text-[18px] leading-6 font-extrabold text-brand-dark ${
          HOW_IT_WORKS_LIVE ? '' : 'invisible'
        }`}
      >
        {link}
        {/* D9: the arrow is drawn in black, not #17270D. */}
        <Arrow className="text-black" />
      </span>
    )}
  </div>
)

const Gallery = () => (
  <section id="features" className="bg-brand-pageFill">
    <div className="max-w-[640px] canvas:max-w-canvas mx-auto px-5 canvas:px-0 pt-[54px] canvas:pt-20 pb-[54px] canvas:pb-[100px]">
      <h2 className="text-[28px] leading-9 canvas:text-[40px] canvas:leading-10 font-extrabold text-center">
        <span className="text-brand-accent">Analyze Smarter.</span>{' '}
        <br className="canvas:hidden" />
        <span className="text-brand-dark">Move Deals Forward.</span>
      </h2>

      {/* Canvas: 54 from the heading to the first card, 72 between cards, 100 to the FAQs. The
          image side of each row sits 148 from the frame edge, the text side 188. */}
      <div className="mt-[2px] canvas:mt-[54px] flex flex-col gap-7 canvas:gap-[72px]">
        {BLOCKS.map((block) => (
          <div
            key={block.title}
            className={`flex flex-col canvas:items-center canvas:gap-12 canvas:w-[1104px] ${
              block.imageRight ? 'canvas:flex-row-reverse canvas:ml-[68px]' : 'canvas:flex-row canvas:ml-[28px]'
            }`}
          >
            <GalleryImage {...block} />
            <GalleryText {...block} />
          </div>
        ))}
      </div>
    </div>
  </section>
)

export default Gallery
